/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useHttp from 'hooks/http.hook';
import { CoreAPI_URLs } from 'constants/http.const';
import { AzureCommunicationTokenInterface, ChatMessageInterface, HeaderEnum, JoinVideochatResponseInterface, NamesParticipantsInterface } from 'medicalsystem-shared';
import { 
  CallAgent, 
  CallClient, 
  DeviceManager, 
  Call,
  RemoteParticipant,
  LocalVideoStream,
} from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import VideochatRenderer from '../common/VideochatRenderer';
import { 
  Box, 
  TextField, 
  Typography, 
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import Button from 'components/generals/Button';
import { 
  Videocam, 
  Person,
} from '@mui/icons-material';
import { t } from 'i18next';

/**
 * Guest videochat page component for unauthenticated telemedicine access.
 * Allows external users to join videochat sessions via access code link.
 * 
 * Features:
 * - Access code-based session joining
 * - Guest name entry before joining
 * - Azure Communication Services integration
 * - Full video calling capabilities without authentication
 * - Audio/video device management
 * - Camera and microphone selection
 * - Real-time participant management
 * - Integrated text chat
 * - Call state management
 * - Connection error handling
 * - Session expiry detection
 * - Automatic role assignment (patient/provider)
 * - Call reconnection capability
 * - Responsive video layout
 * - Loading states for appointment info
 * - Call end detection
 * - Cleanup on unmount
 * 
 * Guest Join Flow:
 * 1. User receives access code link via email/SMS
 * 2. Opens link in browser (no login required)
 * 3. Enters display name
 * 4. Validates access code with backend
 * 5. Receives Azure Communication token
 * 6. Joins group call
 * 7. Full video call participation
 * 
 * Session Management:
 * - Validates access code on load
 * - Fetches session information
 * - Retrieves appointment details (patient/provider names)
 * - Assigns guest role automatically
 * - Checks session active status
 * - Handles session expiry
 * - Enables rejoin if call ends
 * 
 * Video Management:
 * - Local video preview before joining
 * - Remote participant video streams
 * - Camera selection dropdown
 * - Enable/disable video
 * - Video stream cleanup
 * 
 * Audio Management:
 * - Microphone mute/unmute
 * - Audio device selection
 * - Speaker management
 * - Remote participant audio
 * 
 * Call States:
 * - None: Not connected
 * - Connecting: Joining call
 * - Connected: Active call
 * - Ringing: Call incoming
 * - Disconnecting: Ending call
 * - Disconnected: Call ended
 * 
 * Chat Integration:
 * - Real-time text messaging
 * - Message history
 * - Participant identification
 * - Unread indicators
 * 
 * Error Handling:
 * - Invalid access code
 * - Expired session
 * - Connection failures
 * - Device access errors
 * - Network issues
 * - Session not found
 * 
 * Cleanup:
 * - Removes event listeners
 * - Disposes video streams
 * - Hangs up call
 * - Clears state
 * - Releases devices
 * 
 * Notes:
 * - No authentication required
 * - Temporary access only
 * - Session-based permissions
 * - Limited to invited guests
 * - Single-use access codes
 * 
 * @returns {JSX.Element} Guest videochat interface
 */
const GuestVideochatPage: React.FC = () => {
  const { accessCode } = useParams<{ accessCode: string }>();
  const navigate = useNavigate();
  const [isLoading, error, sendRequest] = useHttp();
  const [displayName, setDisplayName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [assignedRole, setAssignedRole] = useState<'patient' | 'provider' | null>(null);
  const [callHasEnded, setCallHasEnded] = useState(false);
  const [canRejoin, setCanRejoin] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [appointmentInfo, setAppointmentInfo] = useState<NamesParticipantsInterface | null>(null);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(true);
  const [call, setCall] = useState<Call | null>(null);
  const [callAgent, setCallAgent] = useState<CallAgent | null>(null);
  const [deviceManager, setDeviceManager] = useState<DeviceManager | null>(null);
  const [callState, setCallState] = useState<string>('None');
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [chatMessages, setChatMessages] = useState<ChatMessageInterface[]>([]);
  const [localVideoStream, setLocalVideoStream] = useState<LocalVideoStream | null>(null);
  const [hasLeftCall, setHasLeftCall] = useState(false);
  const [backendGroupId, setBackendGroupId] = useState<string | null>(null);
  const [stateChangedHandler, setStateChangedHandler] = useState<(() => void) | null>(null);
const [participantsUpdatedHandler, setParticipantsUpdatedHandler] = useState<(() => void) | null>(null);

const cleanupCall = useCallback(async () => {
  if (call) {
    if (stateChangedHandler) {
      call.off('stateChanged', stateChangedHandler);
      setStateChangedHandler(null);
    }
    if (participantsUpdatedHandler) {
      call.off('remoteParticipantsUpdated', participantsUpdatedHandler);
      setParticipantsUpdatedHandler(null);
    }
    await call.hangUp();
    setCall(null);
    setCallState('None');
    setParticipants([]);
  }
  setLocalVideoStream(null);
  setIsVideoEnabled(false);
  setIsAudioEnabled(true);
}, [call, stateChangedHandler, participantsUpdatedHandler]);

const fullCleanup = useCallback(async () => {
  if (call) {
    if (stateChangedHandler) {
      call.off('stateChanged', stateChangedHandler);
    }
    if (participantsUpdatedHandler) {
      call.off('remoteParticipantsUpdated', participantsUpdatedHandler);
    }
    
    await call.hangUp();
    setCall(null);
    setCallState('None');
    setParticipants([]);
  }
  if (callAgent) {
    await callAgent.dispose();
    setCallAgent(null);
  }
  if (deviceManager) {
    setDeviceManager(null);
  }
  setLocalVideoStream(null);
  setIsVideoEnabled(false);
  setIsAudioEnabled(true);
  setStateChangedHandler(null);
  setParticipantsUpdatedHandler(null);
}, [call, callAgent, deviceManager, stateChangedHandler, participantsUpdatedHandler]);

  useEffect(() => {
    return () => {
      fullCleanup();
    };
  }, []);

  useEffect(() => {
    if (!accessCode) {
      setLocalError(t('guestVideochatPage.invalidAccessCode'));
      setIsLoadingAppointment(false);
      return;
    }
    const loadAppointmentInfo = async () => {
      setIsLoadingAppointment(true);
      setLocalError(null);

      try {
        const validateUrl = CoreAPI_URLs.VIDEOCHAT_GUEST_VALIDATE;
        
        await sendRequest(
          validateUrl,
          (validationData: { 
            valid: boolean; 
            appointmentId?: string; 
            patientName?: string;
            providerName?: string;
            guestLink?: string;
          }) => {
            
            if (validationData.valid && validationData.appointmentId) {
              const participantData: NamesParticipantsInterface = {
                patientName: validationData.patientName || 'Patient',
                providerName: validationData.providerName || 'Provider',
                appointmentId: validationData.appointmentId || '',
              };
              
              setAppointmentInfo(participantData);
              setIsLoadingAppointment(false);
            } else {
              setLocalError(t('guestVideochatPage.invalidAccessCode'));
              setIsLoadingAppointment(false);
            }
          },
          'GET',
          null,
          [
            { header: HeaderEnum.RESOURCE_ID, value: accessCode }
          ]
        );
        
      } catch (err) {
        console.error('[GuestVideochatPage] Error during validation:', err);
        setLocalError(err as string);
        setIsLoadingAppointment(false);
      }
    };

    loadAppointmentInfo();
  }, [accessCode, sendRequest]);

  const joinVideochat = async () => {
    if (!displayName.trim()) {
      setLocalError(t('guestVideochatPage.nameError'));
      return;
    }
    if (!appointmentInfo) {
    setLocalError(t('guestVideochatPage.appointmentInfoError'));
    return;
  }

  setLocalError(null);
  setIsJoining(true);
  const timeoutId = setTimeout(() => {
    setLocalError(t('guestVideochatPage.timeoutError'));
    setIsJoining(false);
  }, 30000);
  try {
    const finalDisplayName = displayName.trim();
    const apiUrl = CoreAPI_URLs.VIDEOCHAT_GUEST_JOIN_BY_CODE;
    const requestBody = {
      displayName: finalDisplayName,
    };
    const headers = [
      { header: HeaderEnum.RESOURCE_ID, value: accessCode },
      { header: 'guest-name', value: finalDisplayName }
    ];
    await sendRequest(
      apiUrl,
      (data: JoinVideochatResponseInterface) => {
        clearTimeout(timeoutId);
        setBackendGroupId(data.groupId);
        initializeACS(data.communicationToken, data.groupId, finalDisplayName);
        
        setAssignedRole('patient');
        setHasJoined(true);
        setIsJoining(false);
        
      },
      'POST',
      JSON.stringify(requestBody),
      headers,
      {
        applyErrorFn: (errorMessage: string) => {
          clearTimeout(timeoutId);
          setLocalError(errorMessage);
          setIsJoining(false);
        }
      }
    );
    } catch (error) {
      clearTimeout(timeoutId);
      setLocalError(error as string)
    }
  };
  const initializeACS = async (tokenData: AzureCommunicationTokenInterface, groupId: string, finalDisplayName: string) => {
    try {
      const callClient = new CallClient();
      const tokenCredential = new AzureCommunicationTokenCredential(tokenData.token);
      const agent = await callClient.createCallAgent(tokenCredential, {
        displayName: finalDisplayName
      });
      const devManager = await callClient.getDeviceManager();
      setCallAgent(agent);
      setDeviceManager(devManager);  
      const groupCallLocator = { groupId };
      const groupCall = agent.join(groupCallLocator);
      setCall(groupCall);    
      const stateHandler = () => {
        setCallState(groupCall.state);
        if (groupCall.state === 'Connected') {
          setParticipants([...groupCall.remoteParticipants]);
        }
        if (groupCall.state === 'Disconnected') {
          setCallHasEnded(true);
          setCanRejoin(true);
        }
      };
      
      const participantsHandler = () => {
        setParticipants([...groupCall.remoteParticipants]);
      };
      groupCall.on('stateChanged', stateHandler);
      groupCall.on('remoteParticipantsUpdated', participantsHandler);
      setStateChangedHandler(() => stateHandler);
      setParticipantsUpdatedHandler(() => participantsHandler);
    } catch (err) {
      setLocalError(err as string);
      setIsJoining(false);
    }
  };
  const toggleVideo = async () => {
    if (!call || !deviceManager) return;
    try {
      if (isVideoEnabled && localVideoStream) {
        await call.stopVideo(localVideoStream);
        setIsVideoEnabled(false);
        setLocalVideoStream(null);
      } else {
        const cameras = await deviceManager.getCameras();
        if (cameras.length > 0) {
          const videoStream = new LocalVideoStream(cameras[0]);
          await call.startVideo(videoStream);
          setIsVideoEnabled(true);
          setLocalVideoStream(videoStream);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };
  const toggleAudio = async () => {
    if (!call) return;
    
    try {
      if (isAudioEnabled) {
        await call.mute();
        setIsAudioEnabled(false);
      } else {
        await call.unmute();
        setIsAudioEnabled(true);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const leaveCall = async () => {
    try {
      await cleanupCall();
      setHasLeftCall(true);
      setCallHasEnded(false);
      setCanRejoin(true);
    } catch (err) {
      console.error('Error leaving call:', err);
    }
  };
  const getCurrentUserDisplayName = () => {
    if (assignedRole === 'patient') {
      return appointmentInfo?.patientName || displayName;
    } else if (assignedRole === 'provider') {
      return appointmentInfo?.providerName || displayName;
    }
    return displayName;
  };
  const sendChatMessage = useCallback(async (message: string) => {
    const finalDisplayName = assignedRole === 'patient' 
      ? appointmentInfo?.patientName || displayName
      : assignedRole === 'provider' 
        ? appointmentInfo?.providerName || displayName
        : displayName;

    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const chatMessage: ChatMessageInterface = {
      id: messageId,
      sender: finalDisplayName,
      message: message.trim(),
      timestamp: new Date(),
      appointmentId: appointmentInfo?.appointmentId || '',
      isOwn: true
    };

    try {
      setChatMessages(prev => [...prev, chatMessage]);

      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_SEND_MESSAGE_GUEST,
        (data) => null,
        'POST',
        JSON.stringify({
          id: messageId,
          message: chatMessage.message,
          sender: chatMessage.sender,
          timestamp: chatMessage.timestamp.toISOString(),
          accessCode: accessCode
        }),
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentInfo?.appointmentId || '' },
          { header: 'Content-Type', value: 'application/json' }
        ],
        {
          applyErrorFn: (errorMessage: string) => {
            console.error('Error sending guest message:', errorMessage);
            setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
            setLocalError(t('guestVideochatPage.errorSendingMessage'));
          }
        }
      );

    } catch (error) {
      console.error('Error sending guest message:', error);
      setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
      setLocalError(t('guestVideochatPage.errorSendingMessage'));
    }
  }, [assignedRole, appointmentInfo, displayName, accessCode, sendRequest]);

  const pollForMessagesRobust = useCallback(async () => {
    if (!appointmentInfo?.appointmentId || callState !== 'Connected') return;
  
    try {
      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_GET_MESSAGES_GUEST,
        (data: { messages: ChatMessageInterface[] }) => {
          if (data.messages && data.messages.length > 0) {
            setChatMessages(prev => {
              const existingMessages = new Map();
              prev.forEach(msg => {
                const key1 = msg.id;
                const key2 = `${msg.timestamp.getTime()}_${msg.sender}_${msg.message}`;
                existingMessages.set(key1, true);
                existingMessages.set(key2, true);
              });
            
              const incomingMessages = data.messages
                .map(msg => ({
                  ...msg,
                  timestamp: new Date(msg.timestamp),
                  isOwn: msg.sender === getCurrentUserDisplayName()
                }))
                .filter(msg => {
                  const key1 = msg.id;
                  const key2 = `${new Date(msg.timestamp).getTime()}_${msg.sender}_${msg.message}`;
                  return !existingMessages.has(key1) && !existingMessages.has(key2);
                });
            
              if (incomingMessages.length === 0) {
                return prev;
              }
            
              const updatedMessages = [...prev, ...incomingMessages];
              return updatedMessages.sort((a, b) => 
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              );
            });
          }
        },
        'GET',
        undefined,
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentInfo.appointmentId },
        ]
      );
    } catch (error) {
      console.error('Error polling for guest messages:', error);
    }
  }, [appointmentInfo?.appointmentId, accessCode, sendRequest, callState, getCurrentUserDisplayName]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
  
    if (appointmentInfo?.appointmentId && callState === 'Connected') {
      pollInterval = setInterval(pollForMessagesRobust, 2000);
      pollForMessagesRobust();
    }
  
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [appointmentInfo?.appointmentId, callState, pollForMessagesRobust]);

  const getRemoteParticipantName = () => {
    if (assignedRole === 'patient') {
      return appointmentInfo?.providerName || 'Doctor';
    } else if (assignedRole === 'provider') {
      return appointmentInfo?.patientName || 'Patient';
    }
    return appointmentInfo?.providerName || 'Doctor';
  };

  const getParticipantName = (participant: RemoteParticipant) => {
    if (participant && participant.displayName) {
      return participant.displayName;
    }
    if (appointmentInfo?.patientName && appointmentInfo?.providerName) {
      const currentUser = getCurrentUserDisplayName();
      if (currentUser === appointmentInfo.patientName) {
        return appointmentInfo.providerName;
      } else {
        return appointmentInfo.patientName;
      }
    }
    return t('guestVideochatPage.remoteParticipant');
  };

  const closeVideochat = () => {
    navigate('/');
  };

  const joinCall = async () => {
    if (!callAgent || !backendGroupId) return;
    await cleanupCall(); 
    setHasLeftCall(false);
    setLocalError(null);
    setIsJoining(true);
    try {
      const groupCallLocator = { groupId: backendGroupId };
      const groupCall = callAgent.join(groupCallLocator);
      setCall(groupCall);
      
      groupCall.on('stateChanged', () => {
        setCallState(groupCall.state);
        if (groupCall.state === 'Connected') {
          setIsJoining(false);
        }
        if (groupCall.state === 'Disconnected') {
          setCallHasEnded(true);
          setCanRejoin(true);
        }
      });
      
      groupCall.on('remoteParticipantsUpdated', () => {
        setParticipants([...groupCall.remoteParticipants]);
      });
      
    } catch (error) {
      console.error('Error joining call:', error);
      setIsJoining(false);
      setLocalError('Error joining the call');
      setHasLeftCall(true);
    }
  };

  const rejoinCall = async () => {
    if (!callAgent || !backendGroupId) return;
    await cleanupCall();
    setCallHasEnded(false);
    setCanRejoin(false);
    setHasLeftCall(false);
    setIsJoining(true);
    setLocalError(null);
    
    try {
      const groupCallLocator = { groupId: backendGroupId };
      const groupCall = callAgent.join(groupCallLocator);
      setCall(groupCall);
      const stateHandler = () => {
        setCallState(groupCall.state);
        if (groupCall.state === 'Connected') {
          setIsJoining(false);
        }
        if (groupCall.state === 'Disconnected') {
          setCallHasEnded(true);
          setCanRejoin(true);
        }
      };
      
      const participantsHandler = () => {
        setParticipants([...groupCall.remoteParticipants]);
      };
      groupCall.on('stateChanged', stateHandler);
      groupCall.on('remoteParticipantsUpdated', participantsHandler);
      setStateChangedHandler(() => stateHandler);
      setParticipantsUpdatedHandler(() => participantsHandler);    
    } catch (error) {
      console.error('Error rejoining call:', error);
      setIsJoining(false);
      setLocalError('Error rejoining the call');
    }
  };  

  if (!accessCode) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error">{t('guestVideochatPage.invalidAccessCode')}</Alert>
      </Box>
    );
  }
  if (isLoadingAppointment) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg,rgb(41, 86, 121) 0%,rgb(49, 122, 196) 100%)'
      }}>
        <Card sx={{ maxWidth: 400, width: '100%', m: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('guestVideochatPage.loadingTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('guestVideochatPage.loadingDescription')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }
  if (localError && !appointmentInfo) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg,rgb(41, 86, 121) 0%,rgb(49, 122, 196) 100%)'
      }}>
        <Card sx={{ maxWidth: 400, width: '100%', m: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {localError}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
            {t('guestVideochatPage.tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
  if (!hasJoined) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg,rgb(41, 86, 121) 0%,rgb(49, 122, 196) 100%)'
      }}>
        <Card sx={{ maxWidth: 500, width: '100%', m: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Person sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {t('guestVideochatPage.joinTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('guestVideochatPage.joinDescription')}
              </Typography>
            </Box>
            <TextField
              id="cy-displayName-input"
              fullWidth
              label={t('guestVideochatPage.pleaseEnterName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isJoining && joinVideochat()}
              disabled={isJoining}
              helperText={appointmentInfo ? 
                t('guestVideochatPage.nameRequirements')
                : t('guestVideochatPage.nameError')}
              sx={{ mb: 3 }}
            />
            {localError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {localError}
              </Alert>
            )}
            <Button
            id="cy-join-button"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={joinVideochat}
              disabled={isJoining || !displayName.trim()}
              startIcon={isJoining ? <CircularProgress size={20} /> : <Videocam />}
            >
              {isJoining ? t('guestVideochatPage.joining') : t('guestVideochatPage.joinButton')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'grey.900', 
      color: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        p: 2, 
        bgcolor: 'grey.800', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
          {t('guestVideochatPage.title')} {getCurrentUserDisplayName()}
          {assignedRole && (
            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'primary.300' }}>
              ({assignedRole === 'patient' ? 'Patient' : 'Doctor'})
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
          {t('guestVideochatPage.callState')} {callState}
        </Typography>
      </Box>

      {localError && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" onClose={() => setLocalError(null)}>
            {localError}
          </Alert>
        </Box>
      )}

      <Box sx={{ 
        flex: 1, 
        p: 1,
        minHeight: 'calc(100vh - 120px)',
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          flex: 1,
          minHeight: '600px',
          height: '100%'
        }}>
          <VideochatRenderer
            call={call}
            callAgent={callAgent}
            deviceManager={deviceManager}
            callState={callState}
            participants={participants}
            currentUserName={getCurrentUserDisplayName()}
            getParticipantName={getParticipantName}
            chatMessages={chatMessages}
            onSendMessage={sendChatMessage}
            isVideoEnabled={isVideoEnabled}
            isAudioEnabled={isAudioEnabled}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            onEndCall={callHasEnded ? closeVideochat : leaveCall}
            showChat={true}
            showControls={true}
            showSwapButton={false}
            callHasEnded={callHasEnded}
            canRejoin={canRejoin}
            onRejoinCall={rejoinCall}
            hasLeftCall={hasLeftCall}
            onJoinCall={joinCall}
            className="guest-videochat-renderer"
            isGuestContext={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GuestVideochatPage;